import symbolKeys from '@/constants/symbol.keys';
import { safeInject } from '@/providers/inject';
import { Ethers5Adapter } from '@reown/appkit-adapter-ethers5';
import { defineChain } from '@reown/appkit/networks';
import { createAppKit } from '@reown/appkit/vue';
import { InjectionKey, provide } from 'vue';

export const BERACHAIN = defineChain({
  id: 80094,
  caipNetworkId: 'eip155:80094',
  chainNamespace: 'eip155',
  name: 'Berachain',
  nativeCurrency: {
    decimals: 18,
    name: 'BERA',
    symbol: 'BERA',
  },
  rpcUrls: {
    default: {
      http: ['https://rpc.berachain.com'],
      webSocket: ['wss://rpc.berachain.com'],
    },
  },
  blockExplorers: {
    default: { name: 'Berascan', url: 'https://berascan.com' },
  },
});

export const BEPOLIA = defineChain({
  id: 80069,
  caipNetworkId: 'eip155:80069',
  chainNamespace: 'eip155',
  name: 'Berachain Bepolia',
  nativeCurrency: {
    decimals: 18,
    name: 'BERA',
    symbol: 'BERA',
  },
  rpcUrls: {
    default: {
      http: ['https://bepolia.rpc.berachain.com/'],
      webSocket: ['wss://bepolia.rpc.berachain.com/'],
    },
  },
  blockExplorers: {
    default: {
      name: 'Bepolia BeraTrail',
      url: 'https://bepolia.beratrail.io/',
    },
  },
});

const appkitProvider = () => {
  // 1. Get projectId at https://cloud.reown.com
  const projectId = import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID || 'unknown';

  // 2. Create your application's metadata object
  const metadata = {
    name: 'BurrBear',
    description:
      'A UI that supports core BurrBear protocol functionality. Explore & create pools, manage liquidity, swap tokens and claim incentives.',
    url: window.location.origin, // url must match your domain & subdomain
    icons: ['https://app.burrbear.io/images/logos/burr_64_64.png'],
  };

  // 3. Create a AppKit instance
  createAppKit({
    metadata,
    adapters: [new Ethers5Adapter()],
    networks: [BERACHAIN, BEPOLIA],
    projectId,
    features: {
      analytics: true, // Optional - defaults to your Cloud configuration
    },
  });
};

export type AppkitResponse = ReturnType<typeof appkitProvider>;
export const AppkitProviderSymbol: InjectionKey<AppkitResponse> = Symbol(
  symbolKeys.Providers.AppKit
);

export function provideAppkit(): AppkitResponse {
  const providedAppkit = appkitProvider();
  provide(AppkitProviderSymbol, providedAppkit);
  return providedAppkit;
}

export const useAppkit = (): AppkitResponse => {
  return safeInject(AppkitProviderSymbol);
};
